import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormGroup,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { DialogHandlerService } from '../../core/services/dialog-handler/dialog-handler.service';
import { JoinService } from '../../core/services/join/join.service';

@Component({
    standalone: false,
    selector: 'octo-set-phone',
    templateUrl: './set-phone.component.html',
    styleUrls: ['./set-phone.component.scss'],
})
export class SetPhoneComponent implements OnInit {
    setPhoneForm: UntypedFormGroup;
    submitted = false;
    isSubmitting = false;

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly joinService: JoinService,
        private readonly dialogService: DialogHandlerService,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly cdr: ChangeDetectorRef
    ) {
        this.setPhoneForm = this.fb.group(
            {
                oldPhone: [
                    '',
                    [
                        Validators.required,
                        Validators.pattern(/^\(\d{3}\)\s\d{3}-\d{4}$/),
                    ],
                ],
                newPhone: [
                    '',
                    [
                        Validators.required,
                        Validators.pattern(/^\(\d{3}\)\s\d{3}-\d{4}$/),
                    ],
                ],
                confirmPhone: [
                    '',
                    [
                        Validators.required,
                        Validators.pattern(/^\(\d{3}\)\s\d{3}-\d{4}$/),
                    ],
                ],
            },
            {
                validators: [
                    this.confirmPhoneMatchesValidator,
                    this.newPhoneDifferentValidator,
                ],
            }
        );
    }

    ngOnInit(): void {
        const token =
            this.route.snapshot.paramMap.get('token') ||
            this.route.snapshot.queryParamMap.get('token');
        if (token) {
            sessionStorage.setItem('driverAuthToken', token);
        }
    }

    get form(): { [key: string]: AbstractControl } {
        return this.setPhoneForm.controls;
    }

    onSubmit(): void {
        this.submitted = true;
        this.setPhoneForm.markAllAsTouched();

        if (this.setPhoneForm.invalid || this.isSubmitting) {
            return;
        }

        const oldNumber = this.digitsOnly(this.form['oldPhone'].value);
        const newNumber = this.digitsOnly(this.form['newPhone'].value);
        const confirmNumber = this.digitsOnly(this.form['confirmPhone'].value);

        this.isSubmitting = true;
        this.joinService
            .submitPhoneReset({
                old_phone_number: oldNumber,
                new_phone_number: newNumber,
                confirm_phone_number: confirmNumber,
            })
            .pipe(
                finalize(() => {
                    this.isSubmitting = false;
                    this.cdr.detectChanges();
                })
            )
            .subscribe({
                next: (response: any) => {
                    if (response?.data?.otp_status === 'message_sent') {
                        this.router.navigate(['/login/two_factor_code'], {
                            queryParams: { type: 'phone_update' },
                        });
                        return;
                    }

                    this.dialogService.alert({
                        title: 'Unable to update phone number',
                        message:
                            response?.message ||
                            'Please verify the provided phone numbers and try again.',
                    });
                },
                error: (error: any) => {
                    this.isSubmitting = false;
                    this.cdr.detectChanges();

                    if (error?.status === 401) {
                        this.dialogService.alert({
                            title: 'Link Expired',
                            message:
                                'This link has already been used or has expired. Please initiate a new phone number reset if needed.',
                        });
                        return;
                    }

                    if (
                        error?.error?.ui_message_code ===
                        'initiation_account_locked'
                    ) {
                        this.joinService.setUserError({
                            isLogin: false,
                            noAccess: false,
                            isLocked: true,
                            isReset: false,
                            resetSuccess: false,
                            isLogout: false,
                            invalidLogin: false,
                        });
                        this.router.navigate(['/login/two_factor_code'], {
                            queryParams: { type: 'phone_update', locked: 'true' },
                        });
                        return;
                    }

                    this.dialogService.alert({
                        title: 'Error',
                        message:
                            error?.error?.message ||
                            'Please verify the provided phone numbers and try again.',
                    });
                },
            });
    }

    private digitsOnly(value: string): string {
        return (value || '').replace(/\D/g, '');
    }

    private confirmPhoneMatchesValidator(
        formGroup: AbstractControl
    ): ValidationErrors | null {
        const newPhone = formGroup.get('newPhone')?.value;
        const confirmPhone = formGroup.get('confirmPhone')?.value;

        if (!newPhone || !confirmPhone || newPhone === confirmPhone) {
            return null;
        }

        return { confirmPhoneMismatch: true };
    }

    private newPhoneDifferentValidator(
        formGroup: AbstractControl
    ): ValidationErrors | null {
        const oldPhone = formGroup.get('oldPhone')?.value;
        const newPhone = formGroup.get('newPhone')?.value;

        if (!oldPhone || !newPhone || oldPhone !== newPhone) {
            return null;
        }

        return { newPhoneSameAsOld: true };
    }
}
